import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { news } from "@/mocks/news";
import MovieCard from "@/components/feature/MovieCard";
import NewsListItem from "@/components/feature/NewsListItem";
import SectionHeading from "@/components/base/SectionHeading";
import PageContainer from "@/components/base/PageContainer";
import { usePageMeta } from "@/hooks/usePageMeta";

type ResultType = "all" | "movie" | "tv" | "news";

const TYPE_TABS: { value: ResultType; label: string }[] = [
  { value: "all", label: "全部" },
  { value: "movie", label: "电影" },
  { value: "tv", label: "电视剧" },
  { value: "news", label: "资讯" },
];

function matchMovie(keyword: string) {
  const q = keyword.toLowerCase();
  return movies.filter((m) =>
    [m.title, m.director, m.summary, m.region, ...m.genres, ...m.actors, ...m.tags]
      .join(" ")
      .toLowerCase()
      .includes(q)
  );
}

function matchNews(keyword: string) {
  const q = keyword.toLowerCase();
  return news.filter((n) =>
    [n.title, n.summary, n.author, ...n.tags, ...n.content].join(" ").toLowerCase().includes(q)
  );
}

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const type = (searchParams.get("type") as ResultType) || "all";
  const [input, setInput] = useState(query);

  usePageMeta(
    query ? `搜索「${query}」- 影视影院` : "影视搜索 - 影视影院",
    query
      ? `影视影院搜索「${query}」的结果，包含相关电影、电视剧作品与影视资讯。`
      : "在影视影院搜索电影、电视剧与影视资讯，快速找到想看的内容。",
    "影视搜索,影视影院,电影搜索,电视剧搜索,影视资讯"
  );

  const { matchedMovies, matchedNews } = useMemo(() => {
    if (!query.trim()) return { matchedMovies: [], matchedNews: [] };
    return { matchedMovies: matchMovie(query.trim()), matchedNews: matchNews(query.trim()) };
  }, [query]);

  const showMovies = type === "all" || type === "movie" || type === "tv";
  const showNews = type === "all" || type === "news";

  const moviesResult = matchedMovies.filter((m) =>
    type === "all" ? true : m.type === type
  );
  const newsResult = matchedNews;

  const total =
    (showMovies ? moviesResult.length : 0) + (showNews ? newsResult.length : 0);

  const hotMovies = [...movies].sort((a, b) => b.rating - a.rating).slice(0, 4);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    const next = new URLSearchParams();
    if (value) next.set("q", value);
    if (type !== "all") next.set("type", type);
    setSearchParams(next);
  };

  const changeType = (value: ResultType) => {
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("type");
    else next.set("type", value);
    setSearchParams(next);
  };

  return (
    <div className="w-full bg-background-100">
      <PageContainer className="py-6 md:py-8">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          影视搜索
        </h1>

        <form
          onSubmit={submit}
          className="mt-4 flex flex-col sm:flex-row gap-3"
        >
          <div className="flex items-center flex-1 h-12 rounded-md border border-background-300 bg-background-50 focus-within:border-primary-400 transition-colors">
            <span className="w-12 h-12 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-lg" />
            </span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入影片名称、演员、类型或资讯关键词"
              className="flex-1 h-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-1.5 px-6 py-3 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-sm font-label font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-search-line text-lg" />
            搜索
          </button>
        </form>

        {query ? (
          <>
            <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-foreground-600">
                关键词「<strong className="text-primary-700">{query}</strong>」共找到{" "}
                <strong className="text-primary-700 font-label">{total}</strong> 条结果
              </p>
              <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-200 w-fit max-w-full overflow-x-auto no-scrollbar">
                {TYPE_TABS.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => changeType(tab.value)}
                    className={`px-4 py-1.5 rounded-full text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                      type === tab.value
                        ? "bg-primary-600 text-white"
                        : "text-foreground-600 hover:text-primary-700"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {total > 0 ? (
              <div className="mt-6 flex flex-col gap-8">
                {showMovies && moviesResult.length > 0 && (
                  <section>
                    <SectionHeading title="影视作品" subtitle={`共 ${moviesResult.length} 部`} icon="ri-film-line" />
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                      {moviesResult.map((movie) => (
                        <MovieCard key={movie.id} movie={movie} />
                      ))}
                    </div>
                  </section>
                )}

                {showNews && newsResult.length > 0 && (
                  <section>
                    <SectionHeading title="影视资讯" subtitle={`共 ${newsResult.length} 篇`} icon="ri-article-line" />
                    <div className="flex flex-col gap-3">
                      {newsResult.map((item) => (
                        <NewsListItem key={item.id} item={item} />
                      ))}
                    </div>
                  </section>
                )}
              </div>
            ) : (
              <div className="mt-8 py-16 flex flex-col items-center text-center rounded-lg bg-background-50 border border-background-200">
                <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                  <i className="ri-search-eye-line text-3xl" />
                </span>
                <p className="text-lg font-heading font-bold text-foreground-800">
                  没有找到与「{query}」相关的内容
                </p>
                <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或返回首页浏览热门推荐。</p>
                <Link
                  to="/"
                  className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-sm font-label transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-4-line" />
                  返回首页
                </Link>
              </div>
            )}
          </>
        ) : (
          <div className="mt-8 rounded-lg bg-background-50 border border-background-200 p-8 flex flex-col items-center text-center">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
              <i className="ri-movie-2-line text-3xl" />
            </span>
            <p className="text-lg font-heading font-bold text-foreground-800">输入关键词开始搜索</p>
            <p className="mt-2 text-sm text-foreground-500">
              支持按影片名称、演员、类型或资讯关键词进行模糊搜索。
            </p>
          </div>
        )}

        {/* 热门推荐（无结果时也展示） */}
        <section className="mt-10">
          <SectionHeading title="热门推荐" subtitle="高评分影视作品，也许有你感兴趣的" moreTo="/category?sort=rating" icon="ri-fire-line" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {hotMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>

        <p className="mt-8 text-xs text-foreground-400 text-center leading-relaxed">
          搜索结果为前端模拟演示，未接入真实检索接口；所有影视作品与资讯均为虚构演示数据。
        </p>
      </PageContainer>
    </div>
  );
}